import React, { useEffect, useMemo, useRef, useState } from 'react'
import LoadingIcon from '../../../../../../components/LoadingIcon'

import {
    ContractSupplier,
    ListOrgSupplierItem,
    SearchContractSupplierLResponse,
    CountryList,
} from '../../../types'
import { RegionRf } from '../types'
import ContractSupplierEditPopup from './ContractSupplierEditPopup'

import HoverTooltip from '../../../../../../components/HoverTooltip'

type SupplierSearchItem = ContractSupplier

type Props = {
    open: boolean
    theme: string
    contractGuid?: string
    organizationGuid?: string | null
    oksmList: CountryList[]
    regionRfList: RegionRf[]
    onClose: () => void
    onSelect: (supplier: ContractSupplier) => void
    contractAction: boolean
}

const createEmptyManualSupplier = (
    organizationGuid: string | null | undefined,
    contractGuid: string | undefined
): ContractSupplier => ({
    guid: null,
    supplier_name: null,
    supplier_short_name: null,
    supplier_brand_name: null,
    supplier_additional_full_name: null,
    supplier_inn: null,
    supplier_kpp: null,
    supplier_okpo: null,
    supplier_code: null,
    supplier_additional_code: null,
    supplier_additional_info: null,
    supplier_type: 'L',
    supplier_provider: false,
    supplier_provider_code: null,
    supplier_subcontractor: false,
    supplier_subcontractor_code: null,
    supplier_individual: null,
    supplier_non_resident: false,
    supplier_registration_date: null,
    supplier_tax: true,
    supplier_okopf: null,
    supplier_okopf_name: null,
    supplier_address_egrul_egrip_changed: true,
    supplier_address_country_name: null,
    supplier_address_digital_code: null,
    supplier_address_post_code: null,
    supplier_address_oktmo: null,
    supplier_address_oktmo_name: null,
    supplier_address_region_code: null,
    supplier_address_region_name: null,
    supplier_address_area: null,
    supplier_address_city: null,
    supplier_address_settlement: null,
    supplier_address_street: null,
    supplier_address_email: null,
    supplier_address_phone: null,
    supplier_address_office: null,
    supplier_address_building: null,
    supplier_address_house: null,
    supplier_address_planning_structure: null,
    supplier_address_admin_division: null,
    supplier_provider_include_msp_date: null,
    supplier_is_in_order_173n: true,
    supplier_self_employed: false,
    annotation: null,
    organization_guid: organizationGuid ?? null,
    contract_guid: contractGuid ?? null,
    supplier_guid: null,
    sys_created_at: null,
    sys_updated_at: null,
    temp_id: crypto.randomUUID(),
})

const buildLocalAddress = (item: SupplierSearchItem): string => {
    return [
        item.supplier_address_post_code,
        item.supplier_address_country_name,
        item.supplier_address_region_name,
        item.supplier_address_city,
        item.supplier_address_street,
        item.supplier_address_house,
        item.supplier_address_building,
        item.supplier_address_office,
    ]
        .filter(Boolean)
        .join(', ')
}

const buildExtendedAddress = (item: ListOrgSupplierItem): string => {
    if (item.data.c !== 'cli') return '-'
    return item.data.adr || '-'
}

const ContractSupplierSelectPopup: React.FC<Props> = ({
    open,
    theme,
    contractGuid,
    organizationGuid,
    oksmList,
    regionRfList,
    onClose,
    onSelect,
    contractAction,
}) => {
    const [query, setQuery] = useState('')

    const [loading, setLoading] = useState(false)
    const [items, setItems] = useState<SupplierSearchItem[]>([])
    const [selectedGuid, setSelectedGuid] = useState<string | null>(null)
    const [hasSearched, setHasSearched] = useState(false)

    const [showExtendedButton, setShowExtendedButton] = useState(false)
    const [extendedLoading, setExtendedLoading] = useState(false)
    const [extendedError, setExtendedError] = useState<string | null>(null)
    const [extendedItems, setExtendedItems] = useState<ListOrgSupplierItem[]>(
        []
    )
    const [selectedExtendedKey, setSelectedExtendedKey] = useState<
        string | null
    >(null)
    const [hasExtendedSearched, setHasExtendedSearched] = useState(false)

    const [editPopupOpen, setEditPopupOpen] = useState(false)
    const [editForm, setEditForm] = useState<ContractSupplier | null>(null)
    const [needCreateSupplierGuid, setNeedCreateSupplierGuid] = useState(false)

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const selectedItem = useMemo(
        () => items.find((item) => item.guid === selectedGuid) ?? null,
        [items, selectedGuid]
    )

    const selectedExtendedItem = useMemo(() => {
        return (
            extendedItems.find(
                (item) =>
                    `${item.data.c}_${item.data.id}` === selectedExtendedKey
            ) ?? null
        )
    }, [extendedItems, selectedExtendedKey])

    useEffect(() => {
        if (!open) {
            setQuery('')
            setItems([])
            setSelectedGuid(null)
            setLoading(false)
            setHasSearched(false)

            setShowExtendedButton(false)
            setExtendedLoading(false)
            setExtendedError(null)
            setExtendedItems([])
            setSelectedExtendedKey(null)
            setHasExtendedSearched(false)

            setEditPopupOpen(false)
            setEditForm(null)

            setNeedCreateSupplierGuid(false)
        }
    }, [open])

    useEffect(() => {
        if (!open) return

        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
        }

        const trimmed = query.trim()

        if (trimmed.length <= 7) {
            setItems([])
            setSelectedGuid(null)
            setLoading(false)
            setHasSearched(false)

            setShowExtendedButton(false)
            setExtendedItems([])
            setSelectedExtendedKey(null)
            setExtendedError(null)
            setHasExtendedSearched(false)
            return
        }

        setHasSearched(false)
        setShowExtendedButton(false)
        setExtendedItems([])
        setSelectedExtendedKey(null)
        setExtendedError(null)
        setHasExtendedSearched(false)

        debounceRef.current = setTimeout(async () => {
            try {
                setLoading(true)

                let normalized: SupplierSearchItem[] = []

                const localResponse = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/backend/v1/contract_supplier_info/inn/${encodeURIComponent(trimmed)}`,
                    {
                        method: 'GET',
                        credentials: 'include',
                        headers: {
                            'Content-Type': 'application/json',
                            accept: 'application/json',
                        },
                    }
                )

                if (localResponse.ok) {
                    const localData = await localResponse.json()
                    normalized = Array.isArray(localData) ? localData : []
                } else if (localResponse.status !== 404) {
                    throw new Error('Ошибка поиска контрагентов по ИНН')
                }

                if (!normalized.length) {
                    const fallbackResponse = await fetch(
                        `${process.env.REACT_APP_API_URL}/api/backend/v1/fast_search_db_contract_supplier?text_query=${encodeURIComponent(trimmed)}`,
                        {
                            method: 'GET',
                            credentials: 'include',
                            headers: {
                                'Content-Type': 'application/json',
                                accept: 'application/json',
                            },
                        }
                    )

                    if (!fallbackResponse.ok) {
                        throw new Error('Ошибка поиска контрагентов')
                    }

                    const fallbackData = await fallbackResponse.json()
                    normalized = Array.isArray(fallbackData) ? fallbackData : []
                }

                setItems(normalized)
                setSelectedGuid(normalized[0]?.guid ?? null)
                setShowExtendedButton(normalized.length === 0)
            } catch (error) {
                console.error(error)
                setItems([])
                setSelectedGuid(null)
                setShowExtendedButton(true)
            } finally {
                setLoading(false)
                setHasSearched(true)
            }
        }, 1200)

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current)
            }
        }
    }, [query, open])

    const handleExtendedSearch = async () => {
        const trimmed = query.trim()
        if (trimmed.length <= 7) return

        try {
            setExtendedLoading(true)
            setExtendedError(null)
            setExtendedItems([])
            setSelectedExtendedKey(null)
            setHasExtendedSearched(false)

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/backend/v1/search_contract_supplier_l?query=${encodeURIComponent(trimmed)}&timeout=120`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        accept: 'application/json',
                    },
                }
            )

            if (!response.ok) {
                throw new Error('Ошибка расширенного поиска контрагентов')
            }

            const data: SearchContractSupplierLResponse = await response.json()
            const result = Array.isArray(data.find_suppliers_l)
                ? data.find_suppliers_l
                : []

            setExtendedItems(result)
            setSelectedExtendedKey(
                result.length
                    ? `${result[0].data.c}_${result[0].data.id}`
                    : null
            )
        } catch (error) {
            console.error(error)
            setExtendedError('Не удалось выполнить расширенный поиск')
            setExtendedItems([])
            setSelectedExtendedKey(null)
        } finally {
            setExtendedLoading(false)
            setHasExtendedSearched(true)
        }
    }

    const handleOpenManualAdd = () => {
        setNeedCreateSupplierGuid(false)
        setEditForm(createEmptyManualSupplier(organizationGuid, contractGuid))
        setEditPopupOpen(true)
    }

    const handleOpenExtendedEdit = async (item: ListOrgSupplierItem) => {
        try {
            setExtendedLoading(true)
            setExtendedError(null)

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/backend/v1/search_contract_supplier_l_details?id=${item.data.id}&type=${item.data.c}&query=${encodeURIComponent(query.trim())}&timeout=120`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        accept: 'application/json',
                    },
                }
            )

            if (!response.ok) {
                throw new Error('Ошибка загрузки данных контрагента')
            }

            const data: ContractSupplier = await response.json()

            setNeedCreateSupplierGuid(true)
            setEditForm({
                ...createEmptyManualSupplier(organizationGuid, contractGuid),
                ...data,
                guid: null,
                contract_guid: contractGuid ?? null,
                organization_guid: organizationGuid ?? null,
                supplier_guid: null,
            })
            setEditPopupOpen(true)
        } catch (error) {
            console.error(error)
            setExtendedError('Не удалось загрузить данные контрагента')
        } finally {
            setExtendedLoading(false)
        }
    }

    if (!open) return null

    const createContractSupplier = async (
        supplier: ContractSupplier
    ): Promise<ContractSupplier> => {
        const { temp_id, ...rest } = supplier as ContractSupplier & {
            temp_id?: string
        }

        const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/backend/v1/contract_supplier`,
            {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    accept: 'application/json',
                },
                body: JSON.stringify({
                    ...rest,
                    guid: null,
                    organization_guid: null,
                    contract_guid: null,
                    supplier_guid: null,
                    sys_created_at: null,
                    sys_updated_at: null,
                }),
            }
        )

        if (!response.ok) {
            throw new Error('Не удалось сохранить контрагента в справочник')
        }

        return await response.json()
    }

    return (
        <>
            <div className="fixed top-0 left-0 w-full h-full bg-[#00000050] flex justify-center items-center z-10">
                <div
                    className="p-5 rounded-lg bg-[#e5e5e5] dark:bg-[#262427] w-[1000px] max-h-[800px] flex flex-col gap-4"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex flex-col gap-1">
                        <div className="text-[16px] text-[#181818] dark:text-[#FFFFFF]">
                            Выбор контрагента
                        </div>
                    </div>

                    <div className="flex flex-row gap-3 items-end">
                        <div className="flex flex-col flex-1">
                            <input
                                type="text"
                                inputMode="numeric"
                                maxLength={12}
                                value={query}
                                onChange={(e) => {
                                    const digitsOnly = e.target.value.replace(
                                        /\D/g,
                                        ''
                                    )
                                    setQuery(digitsOnly)
                                }}
                                placeholder="Введите ИНН"
                                className="form-input user-input-placeholder w-full"
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-3">
                        <div className="flex flex-col w-full min-h-[240px] max-h-[240px] rounded bg-[#e4e6e8] dark:bg-[#242225]">
                            <div className="flex flex-row gap-1 w-full h-[30px] p-[5px] items-center justify-center rounded-t text-[11px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                                <div className="flex w-[50px] h-full items-center justify-center text-center">
                                    №
                                </div>
                                <div className="flex flex-1 h-full items-center justify-center text-center">
                                    Наименование
                                </div>
                                <div className="flex w-[160px] h-full items-center justify-center text-center">
                                    ИНН
                                </div>
                                <div className="flex flex-1 h-full items-center justify-center text-center">
                                    Адрес
                                </div>
                            </div>

                            {loading ? (
                                <div className="flex w-full h-full min-h-[180px] items-center justify-center">
                                    <LoadingIcon />
                                </div>
                            ) : items.length > 0 ? (
                                <div className="flex flex-col gap-1 p-1 overflow-auto">
                                    {items.map((item, index) => {
                                        const isSelected =
                                            item.guid === selectedGuid
                                        const address = buildLocalAddress(item)

                                        return (
                                            <button
                                                key={item.guid ?? index}
                                                type="button"
                                                className={`flex flex-row gap-1 w-full min-h-[36px] p-[5px] items-center text-[12px] rounded text-left ${
                                                    isSelected
                                                        ? 'bg-[#ffffff] dark:bg-[#373538] hover:bg-[#ffffff] hover:dark:bg-[#373538]'
                                                        : 'bg-[#f6f6f6] dark:bg-[#2a282b] hover:bg-[#ffffff] hover:dark:bg-[#373538]'
                                                } text-[#181818] dark:text-[#FFFFFF]`}
                                                onClick={() =>
                                                    setSelectedGuid(
                                                        item.guid ?? null
                                                    )
                                                }
                                                onDoubleClick={() =>
                                                    onSelect(item)
                                                }
                                            >
                                                <div className="flex w-5 items-center justify-center ">
                                                    <input
                                                        type="checkbox"
                                                        checked={
                                                            selectedGuid ===
                                                            item.guid
                                                        }
                                                        onChange={() => {
                                                            if (
                                                                item.guid !=
                                                                null
                                                            ) {
                                                                setSelectedGuid(
                                                                    String(
                                                                        item.guid
                                                                    )
                                                                )
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                <div className="flex w-[50px] justify-center">
                                                    {index + 1}
                                                </div>
                                                <div className="flex flex-1">
                                                    <HoverTooltip
                                                        tooltipText={
                                                            item.supplier_short_name ||
                                                            '-'
                                                        }
                                                    >
                                                        <p className="tworow">
                                                            {item.supplier_short_name ||
                                                                '-'}
                                                        </p>
                                                    </HoverTooltip>
                                                </div>
                                                <div className="flex w-[160px] justify-center">
                                                    {item.supplier_inn || '-'}
                                                </div>
                                                <div className="flex flex-1">
                                                    <HoverTooltip
                                                        tooltipText={
                                                            address || '-'
                                                        }
                                                    >
                                                        <p className="tworow">
                                                            {address || '-'}
                                                        </p>
                                                    </HoverTooltip>
                                                </div>
                                            </button>
                                        )
                                    })}
                                </div>
                            ) : (
                                <div className="flex flex-col w-full h-full min-h-[180px] items-center justify-center gap-3 text-[12px] text-[#181818] dark:text-[#FFFFFF] p-4">
                                    {query.trim().length <= 7 ? (
                                        <div>Введите не менее 8 цифр ИНН</div>
                                    ) : hasSearched ? (
                                        <>
                                            <div>Ничего не найдено</div>

                                            {showExtendedButton && (
                                                <button
                                                    className="flex px-4 py-1 min-w-[160px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#d8e7f7] hover:bg-[#e4f0fc] dark:bg-[#31506a] dark:hover:bg-[#3d6483]"
                                                    onClick={
                                                        handleExtendedSearch
                                                    }
                                                    disabled={extendedLoading}
                                                >
                                                    {extendedLoading ? (
                                                        <svg
                                                            className="animate-spin h-4 w-4 text-[#181818] dark:text-[#FFFFFF]"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <circle
                                                                className="opacity-25"
                                                                cx="12"
                                                                cy="12"
                                                                r="10"
                                                                stroke="currentColor"
                                                                strokeWidth="4"
                                                            ></circle>
                                                            <path
                                                                className="opacity-75"
                                                                fill="currentColor"
                                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                                            ></path>
                                                        </svg>
                                                    ) : (
                                                        'Расширенный поиск'
                                                    )}
                                                </button>
                                            )}
                                        </>
                                    ) : (
                                        <div>Ожидание поиска...</div>
                                    )}
                                </div>
                            )}
                        </div>

                        {(extendedLoading ||
                            extendedItems.length > 0 ||
                            extendedError ||
                            hasExtendedSearched) && (
                            <div className="flex flex-col gap-2">
                                <div className="text-sm text-[#181818] dark:text-[#FFFFFF]">
                                    Результаты расширенного поиска
                                </div>

                                <div className="flex flex-col w-full min-h-[240px] max-h-[240px] rounded bg-[#e4e6e8] dark:bg-[#242225]">
                                    <div className="flex flex-row gap-1 w-full h-[30px] p-[5px] rounded-t items-center justify-center text-[11px] text-[#6A6A6A] dark:text-[#8A8A8A] bg-[#d2d5d8] dark:bg-[#333033]">
                                        <div className="flex w-[50px] h-full items-center justify-center text-center">
                                            №
                                        </div>
                                        <div className="flex flex-1 h-full items-center justify-center text-center">
                                            Наименование
                                        </div>
                                        <div className="flex w-[160px] h-full items-center justify-center text-center">
                                            ИНН
                                        </div>
                                        <div className="flex flex-1 h-full items-center justify-center text-center">
                                            Адрес
                                        </div>
                                    </div>

                                    {extendedLoading ? (
                                        <div className="flex w-full h-full min-h-[180px] items-center justify-center">
                                            <LoadingIcon />
                                        </div>
                                    ) : extendedError ? (
                                        <div className="flex w-full h-full min-h-[180px] items-center justify-center text-[12px] text-[#181818] dark:text-[#FFFFFF] p-4">
                                            {extendedError}
                                        </div>
                                    ) : extendedItems.length > 0 ? (
                                        <div className="flex flex-col gap-1 p-1 overflow-auto">
                                            {extendedItems.map(
                                                (item, index) => {
                                                    const rowKey = `${item.data.c}_${item.data.id}`
                                                    const isSelected =
                                                        rowKey ===
                                                        selectedExtendedKey

                                                    return (
                                                        <button
                                                            key={rowKey}
                                                            type="button"
                                                            className={`flex flex-row gap-1 w-full min-h-[36px] p-[5px] items-center text-[12px] rounded text-left ${
                                                                isSelected
                                                                    ? 'bg-[#ffffff] dark:bg-[#373538] hover:bg-[#ffffff] hover:dark:bg-[#373538]'
                                                                    : 'bg-[#f6f6f6] dark:bg-[#2a282b] hover:bg-[#ffffff] hover:dark:bg-[#373538]'
                                                            } text-[#181818] dark:text-[#FFFFFF]`}
                                                            onClick={() =>
                                                                setSelectedExtendedKey(
                                                                    rowKey
                                                                )
                                                            }
                                                            onDoubleClick={() =>
                                                                handleOpenExtendedEdit(
                                                                    item
                                                                )
                                                            }
                                                        >
                                                            <div className="flex w-5 items-center justify-center ">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={
                                                                        isSelected
                                                                    }
                                                                    onChange={() => {
                                                                        if (
                                                                            isSelected
                                                                        ) {
                                                                            setSelectedExtendedKey(
                                                                                rowKey
                                                                            )
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                            <div className="flex w-[50px] justify-center">
                                                                {index + 1}
                                                            </div>
                                                            <div className="flex flex-1">
                                                                <HoverTooltip
                                                                    tooltipText={
                                                                        item.value ||
                                                                        '-'
                                                                    }
                                                                >
                                                                    <p className="tworow">
                                                                        {item.value ||
                                                                            '-'}
                                                                    </p>
                                                                </HoverTooltip>
                                                            </div>
                                                            <div className="flex w-[160px] justify-center">
                                                                {item.data
                                                                    .inn || '-'}
                                                            </div>
                                                            <div className="flex flex-1">
                                                                <HoverTooltip
                                                                    tooltipText={buildExtendedAddress(
                                                                        item
                                                                    )}
                                                                >
                                                                    <p className="tworow">
                                                                        {buildExtendedAddress(
                                                                            item
                                                                        )}
                                                                    </p>
                                                                </HoverTooltip>
                                                            </div>
                                                        </button>
                                                    )
                                                }
                                            )}
                                        </div>
                                    ) : hasExtendedSearched ? (
                                        <div className="flex flex-col w-full h-full min-h-[180px] items-center justify-center gap-3 text-[12px] text-[#181818] dark:text-[#FFFFFF] p-4">
                                            <div>
                                                По расширенному поиску ничего не
                                                найдено
                                            </div>
                                            <button
                                                className="flex px-4 py-1 min-w-[160px] h-6 rounded-[10px] items-center justify-center text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#d8e7f7] hover:bg-[#e4f0fc] dark:bg-[#31506a] dark:hover:bg-[#3d6483]"
                                                onClick={handleOpenManualAdd}
                                            >
                                                Добавить нового
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex w-full h-full min-h-[180px] items-center justify-center text-[12px] text-[#181818] dark:text-[#FFFFFF] p-4">
                                            Нажмите «Расширенный поиск»
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end gap-3">
                            <button
                                className="flex flex-row min-w-[160px] h-6 px-5 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                onClick={handleOpenManualAdd}
                            >
                                Добавить нового
                            </button>

                            <button
                                className="flex flex-row min-w-[120px] h-6 px-4 rounded-[10px] justify-center items-center gap-2 text-xs shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#f6f6f6] dark:bg-[#494849] hover:bg-[#FFFFFF] dark:hover:bg-[#656365]"
                                onClick={onClose}
                            >
                                Отмена
                            </button>

                            {items.length > 0 ? (
                                <button
                                    className={`flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs 
                                        ${!selectedItem ? 'text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#ededed] dark:bg-[#494849]' : 'shadow-md shadow-[#18181830]  text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]'}
                                        `}
                                    disabled={!selectedItem}
                                    onClick={() => {
                                        if (selectedItem) onSelect(selectedItem)
                                    }}
                                >
                                    Выбрать
                                </button>
                            ) : (
                                <button
                                    className={`flex px-4 py-1 min-w-[120px] h-6 rounded-[10px] items-center justify-center text-xs 
                                        ${!selectedExtendedItem ? 'text-[#6a6a6a] dark:text-[#8a8a8a] bg-[#ededed] dark:bg-[#494849]' : 'shadow-md shadow-[#18181830] text-[#181818] dark:text-[#FFFFFF] bg-[#83d2b7] hover:bg-[#8ce5c6] dark:bg-[#187A33] dark:hover:bg-[#1D9A40]'}
                                        `}
                                    disabled={!selectedExtendedItem}
                                    onClick={() => {
                                        if (selectedExtendedItem) {
                                            handleOpenExtendedEdit(
                                                selectedExtendedItem
                                            )
                                        }
                                    }}
                                >
                                    Выбрать
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {editPopupOpen && editForm && (
                <ContractSupplierEditPopup
                    theme={theme}
                    contractSupplierGuid={editForm.guid}
                    contractSupplier={editForm}
                    handleContractSupplierEditPopup={() => {
                        if (needCreateSupplierGuid) {
                            setExtendedError(null)
                        }
                        setEditPopupOpen(false)
                        setEditForm(null)
                        setNeedCreateSupplierGuid(false)
                    }}
                    onSave={async (patch) => {
                        try {
                            const preparedSupplier: ContractSupplier = {
                                ...editForm,
                                ...patch,
                                guid: null,
                                contract_guid: contractGuid ?? null,
                                organization_guid: organizationGuid ?? null,
                            }

                            let createdSupplierGuid: string | null =
                                preparedSupplier.supplier_guid ?? null

                            if (needCreateSupplierGuid) {
                                const createdSupplier =
                                    await createContractSupplier(
                                        preparedSupplier
                                    )
                                createdSupplierGuid =
                                    createdSupplier.guid ?? null
                            }

                            onSelect({
                                ...preparedSupplier,
                                guid: null,
                                supplier_guid: createdSupplierGuid,
                                contract_guid: contractGuid ?? null,
                                organization_guid: organizationGuid ?? null,
                            })

                            setEditPopupOpen(false)
                            setEditForm(null)
                            setNeedCreateSupplierGuid(false)
                            onClose()
                        } catch (error) {
                            console.error(error)
                            setExtendedError('Не удалось сохранить контрагента')
                            throw error
                        }
                    }}
                    oksmList={oksmList}
                    regionRfList={regionRfList}
                    contractAction={contractAction}
                />
            )}
        </>
    )
}

export default ContractSupplierSelectPopup
